import type { ParsedUrlQueryInput } from "querystring";

export type AccommodationCardQuery = string | null | ParsedUrlQueryInput;
type QueryArrayValue = NonNullable<
  Extract<ParsedUrlQueryInput[string], ReadonlyArray<unknown>>
>;

const isEmptyQueryValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (typeof value === "number" && Number.isNaN(value));

const getSanitizedQueryString = (query: string) => {
  const hasQuestionMark = query.startsWith("?");
  const parameters = new URLSearchParams(
    hasQuestionMark ? query.slice(1) : query
  );
  const sanitizedParameters = new URLSearchParams();

  for (const [key, value] of parameters.entries()) {
    if (!isEmptyQueryValue(value)) {
      sanitizedParameters.append(key, value);
    }
  }

  const sanitizedQuery = sanitizedParameters.toString();

  if (!sanitizedQuery) return "";

  return `${hasQuestionMark ? "?" : ""}${sanitizedQuery}`;
};

export function sanitizeAccommodationCardQuery(query: string): string;
export function sanitizeAccommodationCardQuery(query: null): null;
export function sanitizeAccommodationCardQuery(
  query: ParsedUrlQueryInput
): ParsedUrlQueryInput;
export function sanitizeAccommodationCardQuery(
  query: AccommodationCardQuery
): AccommodationCardQuery;
export function sanitizeAccommodationCardQuery(
  query: AccommodationCardQuery
): AccommodationCardQuery {
  if (typeof query === "string") return getSanitizedQueryString(query);
  if (!query) return query;

  const result: ParsedUrlQueryInput = {};

  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) {
      const sanitizedValue = value.filter(
        (item) => !isEmptyQueryValue(item)
      ) as QueryArrayValue;

      if (sanitizedValue.length > 0) {
        result[key] = sanitizedValue;
      }

      continue;
    }

    if (!isEmptyQueryValue(value)) {
      result[key] = value;
    }
  }

  return result;
}

export function sanitizeAccommodationCardHref(href: string): string;
export function sanitizeAccommodationCardHref(href: null): null;
export function sanitizeAccommodationCardHref(): undefined;
export function sanitizeAccommodationCardHref(
  href: string | null | undefined
): string | null | undefined;
export function sanitizeAccommodationCardHref(href?: string | null) {
  if (!href || !href.includes("?")) return href;

  const hashIndex = href.indexOf("#");
  const hrefWithoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : "";
  const queryIndex = hrefWithoutHash.indexOf("?");

  if (queryIndex < 0) return href;

  const pathname = hrefWithoutHash.slice(0, queryIndex);
  const query = getSanitizedQueryString(hrefWithoutHash.slice(queryIndex + 1));

  return `${pathname}${query ? `?${query}` : ""}${hash}`;
}
